import type { CoinbaseEngUsersAccountManagementApiApiWorkspaceMember } from '@cbhq/identity-api-hooks/account-management/types';

const ACCOUNT_MANAGEMENT_LE_ADMIN = 'account_management/legal_entity:admin';
const WORKSPACE_ORG_ADMIN = 'workspace_org_admin';

/**
 * The relevant slice of a GET /organization/v1/workspace/members entry.
 * The API always returns the current user first, so callers pass `members[0]`.
 */
type WorkspaceMemberAccess = Pick<
  CoinbaseEngUsersAccountManagementApiApiWorkspaceMember,
  'legalEntityRoles' | 'roles'
>;

/**
 * Returns true if the current user may manage members (invite or edit roles).
 * Invite and edit share the exact same permission. Requires either:
 * - account_management/legal_entity:admin on at least one legal entity
 *   (lives in `legalEntityRoles[].roles`), or
 * - workspace_org_admin (org-level, lives in the top-level `roles`).
 */
export function canManageMembers(currentUser: WorkspaceMemberAccess | undefined): boolean {
  const legalEntityRoles = currentUser?.legalEntityRoles?.flatMap((ler) => ler.roles ?? []) ?? [];
  const orgRoles = currentUser?.roles ?? [];

  return (
    legalEntityRoles.includes(ACCOUNT_MANAGEMENT_LE_ADMIN) || orgRoles.includes(WORKSPACE_ORG_ADMIN)
  );
}

/**
 * Returns true if the current user may remove a member from the organization.
 * Stricter than managing members: requires the org-level workspace_org_admin role
 * (lives in `members[0].roles`).
 */
export function canRemoveMembers(currentUser: WorkspaceMemberAccess | undefined): boolean {
  return currentUser?.roles?.includes(WORKSPACE_ORG_ADMIN) ?? false;
}
